import {
  makeSimpleClosedTask,
  pick,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const TYPE = "MaturaPyramidFacesVerticesClosedTask";
const KEYS = ["faces", "vertices", "edges"];
const LABELS = { faces: "ścian", vertices: "wierzchołków", edges: "krawędzi" };

function countsFor(solidType, baseSides) {
  return solidType === "prism"
    ? { faces: baseSides + 2, vertices: 2 * baseSides, edges: 3 * baseSides }
    : { faces: baseSides + 1, vertices: baseSides + 1, edges: 2 * baseSides };
}

function makeWrongAnswers(correct) {
  return Array.from(new Set(
    [correct - 1, correct + 1, correct - 2, correct + 2, correct + 3]
      .filter((value) => value > 0 && value !== correct)
  )).slice(0, 3).map(String);
}

export function buildTask({ solidType, baseSides, givenKey, askedKey, variant, fixedOptions = null }) {
  const counts = countsFor(solidType, baseSides);
  const givenValue = counts[givenKey];
  const correctValue = counts[askedKey];
  const solidGenitive = solidType === "prism"
    ? "graniastosłupa prawidłowego"
    : "ostrosłupa prawidłowego";
  const solidShortGenitive = solidType === "prism" ? "graniastosłupa" : "ostrosłupa";
  const formulas = solidType === "prism"
    ? "s=n+2,\\quad w=2n,\\quad k=3n"
    : "s=n+1,\\quad w=n+1,\\quad k=2n";
  const nCalculation = solidType === "prism"
    ? givenKey === "faces"
      ? `n=s-2=${givenValue}-2=${baseSides}`
      : givenKey === "vertices"
        ? `n=\\frac{w}{2}=\\frac{${givenValue}}{2}=${baseSides}`
        : `n=\\frac{k}{3}=\\frac{${givenValue}}{3}=${baseSides}`
    : givenKey === "edges"
      ? `n=\\frac{k}{2}=\\frac{${givenValue}}{2}=${baseSides}`
      : `n=${givenKey === "faces" ? "s" : "w"}-1=${givenValue}-1=${baseSides}`;
  const askedSymbol = askedKey === "faces" ? "s" : askedKey === "vertices" ? "w" : "k";
  const askedFormula = solidType === "prism"
    ? askedKey === "faces"
      ? `s=n+2=${baseSides}+2=${correctValue}`
      : askedKey === "vertices"
        ? `w=2n=2\\cdot${baseSides}=${correctValue}`
        : `k=3n=3\\cdot${baseSides}=${correctValue}`
    : askedKey === "edges"
      ? `k=2n=2\\cdot${baseSides}=${correctValue}`
      : `${askedSymbol}=n+1=${baseSides}+1=${correctValue}`;

  const task = makeSimpleClosedTask({
    type: TYPE,
    taskNumber: "24",
    variant,
    prompt: `Liczba wszystkich ${LABELS[givenKey]} ${solidGenitive} jest równa`,
    promptLatex: String(givenValue),
    promptAfterLatex: `. Liczba wszystkich ${LABELS[askedKey]} tego ${solidShortGenitive} jest równa:`,
    correct: String(correctValue),
    wrong: makeWrongAnswers(correctValue),
    fixedOptions,
    tags: ["stereometria", "graniastoslup", "ostroslup", "elementy-bryly"],
    explanationSteps: [
      {
        title: `Oznaczamy przez n liczbę boków wielokąta w podstawie ${solidShortGenitive}. Korzystamy ze wzorów:`,
        latex: formulas,
      },
      { title: "Z podanej liczby wyznaczamy n.", latex: nCalculation },
      { title: `Obliczamy liczbę ${LABELS[askedKey]}.`, latex: askedFormula },
    ],
    data: {
      solidType,
      baseSides,
      givenKey,
      askedKey,
      givenValue,
      correctValue,
      ...counts,
      maxPoints: 1,
    },
  });

  return {
    ...task,
    source: {
      variant,
      exam: "MATURA",
      session: variant === "original" ? "2024_SIE" : undefined,
      taskNumber: 24,
      basedOn: "CKE-P-2024-SIE-24",
    },
  };
}

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2024_SIE";
  if (original) {
    return buildTask({
      solidType: "pyramid",
      baseSides: 11,
      givenKey: "faces",
      askedKey: "vertices",
      variant: "original",
      fixedOptions: [["A", "10"], ["B", "11"], ["C", "12"], ["D", "13"]],
    });
  }

  const solidType = options.solidType || pick(["pyramid", "prism"]);
  const givenKey = options.givenKey || pick(KEYS);
  const askedKey = options.askedKey || pick(KEYS.filter((key) => key !== givenKey));
  return buildTask({
    solidType,
    baseSides: options.baseSides || pick([3, 4, 5, 6, 7, 8, 9, 10]),
    givenKey,
    askedKey,
    variant: "random",
  });
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedTask as renderTask,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
